<script setup lang="ts">
import { onMounted, ref } from 'vue';

import BrandLogo from './BrandLogo.vue';
import { ApiError, api } from '@/net/api';
import { useSession } from '@/state/session';

/**
 * What `/verify-email?token=…` opens.
 *
 * The link in the registration email points here, and **before this existed it
 * pointed nowhere**: the URL is built server-side from `APP_URL`, this app has no
 * router, and the token was silently dropped on the landing page. With the game
 * gated behind verification that made the email a dead end.
 *
 * **It redeems on mount rather than behind a button.** The player has already
 * expressed intent by clicking a link in their own inbox; asking them to confirm
 * that they meant to confirm is a step with no decision in it. The token is
 * single-use, so a reload after success reports it spent — which is why the
 * failure wording says so rather than implying the link was never valid.
 *
 * **It works signed in or signed out**, and that is the whole reason it is a
 * screen of its own rather than part of the gate. Somebody may open the link in a
 * different browser from the one they registered in; the token identifies the
 * account, so no session is needed. `session.sync()` afterwards is what makes it
 * seamless for the common case — verified in the same browser, it picks up
 * `emailVerified: true` and `App.vue` drops the gate without a sign-in.
 *
 * **It has to be `sync()` and not `refreshSession()`**, and that distinction cost
 * a debugging pass: `refreshSession` deduplicates concurrent callers, so the
 * refresh `App.vue` fires on mount is *already in flight* when this screen asks —
 * and it carries the pre-verification answer. The app went straight back to the
 * gate it had just been let through. See `sync()` in `state/session.ts`.
 */
const emit = defineEmits<{ done: [] }>();
const props = defineProps<{ token: string }>();

const session = useSession();

type State = 'working' | 'verified' | 'failed';

const state = ref<State>('working');
const message = ref('');
/** True when a session was picked up, so the button can say where it goes. */
const signedIn = ref(false);

onMounted(async () => {
  try {
    await api.verifyEmail(props.token);
    state.value = 'verified';

    // Two follow-ups, both allowed to fail silently.
    //
    // This is what lets a player who clicked the link in the browser they
    // registered in continue without signing in again; it fails harmlessly for
    // anybody who opened it elsewhere, and the button then says "Sign in".
    signedIn.value = await session.sync();
    // And the welcome email, which needs a session — nothing on the server *is*
    // the moment of verification, so the client asks. Idempotent.
    if (signedIn.value) await api.welcome().catch(() => {});
  } catch (err) {
    state.value = 'failed';
    message.value =
      err instanceof ApiError
        ? err.message
        : 'Could not reach the server. Try the link again in a moment.';
  }
});
</script>

<template>
  <div class="screen" data-test="verify-screen">
    <div class="panel">
      <BrandLogo class="brand" />

      <template v-if="state === 'working'">
        <h1>Confirming…</h1>
        <p class="tagline">One moment.</p>
      </template>

      <template v-else-if="state === 'verified'">
        <h1 data-test="verify-ok">Address confirmed</h1>
        <p class="tagline">
          That is everything. The garage, the shop, the research tree and the arena
          are open.
        </p>
        <p class="hint">
          New to these? Take <strong>Basic Training</strong> first — six drills, no
          fuel, no payout, and you cannot fail it. It is the first card on the
          Missions tab of the battle picker.
        </p>
        <button type="button" class="primary" data-test="verify-continue" @click="emit('done')">
          {{ signedIn ? 'Open the garage' : 'Sign in' }}
        </button>
      </template>

      <template v-else>
        <h1 data-test="verify-failed">That link did not work</h1>
        <p class="tagline">{{ message }}</p>
        <p class="hint">
          A confirmation link can be used once and lasts 24 hours. If you have
          already used this one your address is confirmed and you can simply sign
          in; otherwise sign in and ask for a fresh one.
        </p>
        <button type="button" class="primary" data-test="verify-continue" @click="emit('done')">
          Continue
        </button>
      </template>
    </div>
  </div>
</template>

<style scoped>
.screen {
  display: grid;
  place-items: center;
  height: 100%;
  overflow-y: auto;
  background: radial-gradient(circle at 50% 30%, #1a222c 0%, var(--steel-900) 65%);
}

.panel {
  width: min(440px, calc(100vw - 2rem));
  margin: 2rem 0;
  padding: 2.25rem;
  border: 1px solid var(--steel-600);
  border-radius: 4px;
  background: rgba(18, 22, 28, 0.9);
}

.brand {
  --brand-logo-height: 1.4rem;
  display: block;
  margin-bottom: 1.5rem;
}

h1 {
  margin: 0;
  font-size: 1.6rem;
  letter-spacing: 0.1em;
  color: #fff;
}

.tagline {
  margin: 0.4rem 0 1.25rem;
  color: var(--steel-400);
  font-size: 0.875rem;
  line-height: 1.5;
}

.hint {
  margin: 0 0 1.25rem;
  padding: 0.7rem 0.85rem;
  border-left: 3px solid var(--steel-600);
  background: rgba(11, 13, 17, 0.6);
  font-size: 0.8rem;
  line-height: 1.55;
  color: var(--steel-400);
}

.hint strong {
  color: var(--steel-200);
}

.primary {
  width: 100%;
  padding: 0.8rem;
  border: none;
  border-radius: 3px;
  background: var(--accent);
  color: var(--steel-900);
  font-weight: 700;
  font-size: 0.9rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
}
</style>
